import { useAuth } from '@app/icare/client/auth'
import { db } from '@app/icare/client/db'
import {
  localPaginatedQueryOptions,
  type ResultTypeFromQueryResult,
} from '@app/icare/client/db/client/localQueryOptions'
import { relation } from '@app/icare/client/db/helpers'
import { TherapyStatusBadge } from '@app/icare/components/status/TherapyStatusBadge'
import { patientRelations } from '@app/icare/db/query/patientRelations'
import { queryKit } from '@app/icare/db/query/queryKit'
import PatientSummary from '@app/icare/entities/patient/components/PatientSummary'
import { PatientStatusEnum } from '@app/icare/entities/patient/schema/Patient'
import type { PatientTherapyRelation } from '@app/icare/entities/patient/schema/PatientTherapy'
import { type TherapyType, TherapyTypeEnum } from '@app/icare/entities/therapy/schema/TherapyType'
import { useRouteTour } from '@app/icare/module/tour/useRouteTour'
import { mapPatientTherapyToTherapyStatus, TherapyStatusEnum } from '@app/icare/shared/api/therapy.type'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Space, Table, Tour } from 'antd'
import { sql } from 'kysely'
import { useRef, useState } from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/_dashboards/my-patients')({
  component: RouteComponent,
})

const FilterSchema = QueryBaseSchema.extend({
  sortBy: z.enum(['patient.personal.lastName']).default('patient.personal.lastName').nullish(),
  'patient.status.value': Q.in(z.enum(PatientStatusEnum.$.zodEnum())).nullish(),
})

const INITIAL_FILTERS = {
  'patient.status.value': F.in([PatientStatusEnum.new, PatientStatusEnum.open]),
}
const useMyPatientsQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: INITIAL_FILTERS,
  },
})

function RouteComponent() {
  const { current } = useAuth()
  const queryStore = useMyPatientsQueryStore(useShallow(s => s))
  const [open, setOpen] = useState(false)

  const myPatientsParams = queryKit.parseQuery(queryStore, { paginationMode: 'offset' })
  const therapyTypeFilter = myPatientsParams.filters.find(f => f.field === 'therapies.type')
  const therapyTypeValues: TherapyType[] =
    therapyTypeFilter?.operator === 'in' && Array.isArray(therapyTypeFilter.value)
      ? therapyTypeFilter.value.flatMap(value => (TherapyTypeEnum.$.is(value) ? [value] : []))
      : therapyTypeFilter?.operator === 'eq' && TherapyTypeEnum.$.is(therapyTypeFilter.value)
        ? [therapyTypeFilter.value]
        : []

  const myPatientsBaseParams = {
    ...myPatientsParams,
    filters: myPatientsParams.filters.filter(f => f.field !== 'therapies.type'),
  }

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patient')
        .selectAll('patient')
        .$if(!!current?.id, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patientCareManager')
                .select('patientCareManager.id')
                .whereRef('patientCareManager.patientId', '=', 'patient.id')
                .$call(relation.isActive('patientCareManager'))
                .where(sql<boolean>`${sql.ref('patientCareManager.toId')} = ${current?.id}`),
            ),
          ),
        )
        .select([patientRelations.therapies(), patientRelations.careManagers(), patientRelations.adTeams()])
        .$if(therapyTypeValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patientTherapy')
                .select('patientTherapy.id')
                .whereRef('patientTherapy.patientId', '=', 'patient.id')
                .$call(relation.isActive('patientTherapy'))
                .where('patientTherapy.type', 'in', therapyTypeValues),
            ),
          ),
        )
        .$call(queryKit.all(myPatientsBaseParams)),
      params: myPatientsParams,
    }),
  )

  const columns = useColumnDef<ResultTypeFromQueryResult<typeof data>>(
    () => [
      {
        key: 'personal.lastName',
        title: <Trans>Patientenakte</Trans>,
        fixed: 'start',
        sorter: true,
        render: (_, record) => <FormattedPatientNameAndAddress patient={record} />,
      },
      {
        key: 'patient.therapies',
        title: <Trans>Therapien</Trans>,
        sorter: false,
        render: (_, record) => (
          <div className="flex flex-wrap gap-2">
            {(record.therapies || [])
              .filter(
                (therapy: PatientTherapyRelation) =>
                  mapPatientTherapyToTherapyStatus(therapy, record.deceased) !== TherapyStatusEnum.ENDED,
              )
              .map((therapy: PatientTherapyRelation) => (
                <div key={therapy.id}>
                  <TherapyStatusBadge
                    className="mx-0"
                    value={mapPatientTherapyToTherapyStatus(therapy, record.deceased)}
                  >
                    {TherapyTypeEnum.$.getLabel(therapy.type)}
                  </TherapyStatusBadge>
                </div>
              ))}
          </div>
        ),
      },
      {
        key: 'actions',
        title: <Trans>Aktionen</Trans>,
        fixed: 'end',
        sorter: false,
        render: (_, record) => (
          <ButtonLink to="/patienten/$id" params={{ id: record.id }} size="small">
            <Trans>zur Akte</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    useMyPatientsQueryStore,
  )

  const tableRef = useRef<HTMLDivElement>(null)

  const tour = useRouteTour('orders-table', [
    {
      title: t`Meine Patienten`,
      description: t`Hier siehst du eine Tabelle mit deinen relevanten Datensätzen. Klicke auf eine Zeile, um weitere Details zu sehen.`,
      target: () => tableRef.current,
    },
  ])

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space size="small">
            <SearchInput
              size="small"
              value={queryStore.meta.searchRawValue}
              onChange={e => queryStore.setSearch(e.target.value)}
            />
            <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
          </Space>
        ),
      },
    },
    [queryStore.meta.searchRawValue, queryStore.setSearch, queryStore.meta.activeFilterCount, setOpen, queryStore],
  )

  return (
    <QueryFilterWrapper
      queryStore={useMyPatientsQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'patient.status.value',
          label: <Trans>Status der Patientenakte</Trans>,
          mode: 'multiple',
          options: PatientStatusEnum.$.options,
        },
        {
          name: 'therapies.type',
          label: <Trans>Therapieform</Trans>,
          mode: 'multiple',
          options: TherapyTypeEnum.$.options,
        },
      ]}
    >
      <div ref={tableRef}>
        <Table
          dataSource={data?.data ?? []}
          loading={isFetching}
          columns={columns}
          rowKey="id"
          pagination={data}
          size="small"
          scroll={{ x: 'max-content' }}
          onChange={queryStore.onChange}
          expandable={{
            expandedRowRender: record => <PatientSummary patient={record as never} />,
          }}
        />
      </div>
      <Tour {...tour} />
    </QueryFilterWrapper>
  )
}
